import { PropsWithChildren } from 'react';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import { useResourcePreviewChanges } from './changeContext';
import ChangeDetailsDropdown from './ChangeDetailsDropdown';
import { findPreviewFieldChange } from './fieldChange';

export interface ResourcePreviewFieldProps extends PropsWithChildren {
    displayMode?: 'inline' | 'reference';
    field: string;
    resourceId?: string;
    resourceType: string;
}

/** Decorates an explicitly identified field, never guesses which repeated text belongs to a change. */
const ResourcePreviewField = ({
    children,
    displayMode = 'inline',
    field,
    resourceId,
    resourceType,
}: ResourcePreviewFieldProps) => {
    const { token } = useAntDesignTheme();
    const preview = useResourcePreviewChanges();
    const match = findPreviewFieldChange(preview, field, resourceId, resourceType);
    const change = match?.change;
    if (!change) {
        return <>{children}</>;
    }
    const highlight = preview?.highlightChanges;
    const reference = displayMode === 'reference';

    const comparison = (
        <ChangeDetailsDropdown changes={[change]} enabled={highlight}>
            <span
                data-change-id={change.id}
                tabIndex={-1}
                style={{
                    alignItems: highlight ? 'center' : undefined,
                    borderRadius: token.borderRadiusSM,
                    display: reference && !highlight ? 'none' : highlight ? 'inline-flex' : undefined,
                    flexWrap: highlight ? 'wrap' : undefined,
                    gap: highlight ? '0.375rem' : undefined,
                    maxWidth: highlight ? '100%' : undefined,
                    minWidth: highlight ? 0 : undefined,
                    verticalAlign: highlight ? 'middle' : undefined,
                }}
            >
                {highlight && change.type !== 'added' && (
                    <del
                        style={{
                            background: token.colorFillSecondary,
                            color: token.colorTextSecondary,
                            boxSizing: 'border-box',
                            minWidth: 0,
                            overflowWrap: 'anywhere',
                            padding: '0.0625rem 0.25rem',
                            whiteSpace: 'normal',
                        }}
                    >
                        {change.before}
                    </del>
                )}
                {(!reference || change.type !== 'removed') && (
                    <span
                        style={{
                            alignItems: highlight ? 'center' : undefined,
                            background: highlight && change.type !== 'removed' ? token.colorPrimaryBg : undefined,
                            boxSizing: highlight ? 'border-box' : undefined,
                            color: highlight && change.type !== 'removed' ? token.colorPrimaryText : undefined,
                            borderRadius: token.borderRadiusSM,
                            display: highlight ? 'inline-flex' : undefined,
                            maxWidth: highlight ? '100%' : undefined,
                            minWidth: highlight ? 0 : undefined,
                            paddingInline: highlight && change.type !== 'removed' ? '0.25rem' : undefined,
                        }}
                    >
                        {reference ? change.after : children}
                    </span>
                )}
            </span>
        </ChangeDetailsDropdown>
    );

    // Rich references retain their own identity styling and hover card beside, never inside, the comparison.
    return reference ? (
        <span
            style={{
                display: highlight ? 'inline-flex' : undefined,
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '0.5rem',
                minWidth: 0,
                maxWidth: '100%',
            }}
        >
            {comparison}
            <span style={{ display: 'inline-flex', minWidth: 0, maxWidth: '100%' }}>{children}</span>
        </span>
    ) : (
        comparison
    );
};

export default ResourcePreviewField;
